import {
  Box,
  Container,
  DialogContent,
  Grid,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import InfiniteScroll from "react-infinite-scroll-component";

import useApi from "../../hooks/useApi.js";
import { visibilityOptions } from "../../utils/utils.js";
import ExploreSecondaryHeader from "./SecondaryHeader.js";
import ActivityJournal from "./ActivityJournal.js";
import { useDetailNavigation } from "../../hooks/useDetailNavigation.js";
import EmptyActivity from "./ActivityEmpty.js";
import ActivityTutorial from "./ActivityTutorial.js";
import ActivitySkeleton from "./ActivitySkeleton.js";
import Loading from "../../components/Loading.js";
import Offline from "../../components/Offline.js";
import StandardDialog from "../../components/StandardDialog.js";
import UserSearch from "./UserSearch.js";
import MotionInView from "../../components/MotionInView.js";
import { useInitialBatchSize } from "../../hooks/useInitialBatchSize.js";
import { useFillViewport } from "../../hooks/useFillViewport.js";
import { useThemeContext } from "../../context/themeContext.js";
import { useAuth } from "../../context/authContext.js";
import { useOutletContext } from "react-router-dom";
import {
  HeaderToggleButton,
  HeaderToggleButtonGroup,
} from "../../components/CustomMUI.js";

// Persists the user's last-chosen feed scope so the page reopens where they left off.
const SCOPE_STORAGE_KEY = "curate_explore_feed_scope";

// Reuse the visibility icons: "everyone" → Public, "following" → the followers/group icon.
const visibilityIconByValue = Object.fromEntries(
  visibilityOptions.map((o) => [o.value, o.icon]),
);
const SCOPE_OPTIONS = [
  {
    value: "everyone",
    label: "Everyone",
    icon: visibilityIconByValue.everyone,
  },
  {
    value: "following",
    label: "Following",
    icon: visibilityIconByValue.followers,
  },
];

/**
 * Activity feed — /explore/activity. Toggles between a global "Everyone" discovery
 * feed and a "Following" feed; the choice is remembered in localStorage.
 * contentTypeFilter is passed as a prop by ExploreDispatcher (from the URL segment).
 */
export default function ExploreActivity({ contentTypeFilter = "all" }) {
  const { user, openAuthDialog } = useAuth();
  const { toggleDrawer, drawerWidth } = useOutletContext();
  const { isTouchDevice } = useThemeContext();
  const { fetchActivityFeed } = useApi();
  const { openContent } = useDetailNavigation();

  // On very narrow phones (≤360px, e.g. Galaxy S8) the title + toggle labels run
  // out of room, so collapse the toggle to icons only at this width and below.
  const compactToggle = useMediaQuery("(max-width:360px)");

  const [showTutorial, setShowTutorial] = useState(true);
  const [userSearchOpen, setUserSearchOpen] = useState(false);

  // Default to the global feed on first visit; otherwise honour the stored choice.
  // Logged-out visitors only get the global feed, so ignore any stored "following".
  const [scope, setScope] = useState(() =>
    user ? localStorage.getItem(SCOPE_STORAGE_KEY) || "everyone" : "everyone",
  );

  const handleScopeChange = (_event, nextScope) => {
    if (!nextScope || nextScope === scope) return;
    // The Following feed needs an account — prompt sign-up instead of switching.
    if (nextScope === "following" && !user) {
      openAuthDialog("Following accounts");
      return;
    }
    setScope(nextScope);
    localStorage.setItem(SCOPE_STORAGE_KEY, nextScope);
  };

  // Coerce back to the global feed if the viewer is (or becomes) logged out.
  useEffect(() => {
    if (!user && scope !== "everyone") setScope("everyone");
  }, [user, scope]);

  const feedInfiniteQuery = useInfiniteQuery({
    queryKey: ["infFeed", contentTypeFilter, scope],
    queryFn: ({ pageParam }) =>
      fetchActivityFeed({
        pageParam,
        contentType: contentTypeFilter,
        perPage: 5,
        scope,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    // Override the global staleTime: Infinity so the global refetchOnMount refetches
    // the feed on every navigation to /explore/activity (discovery feed wants fresh data).
    staleTime: 0,
  });

  const feed = React.useMemo(
    () => feedInfiniteQuery.data?.pages.flatMap((page) => page.items) || [],
    [feedInfiniteQuery.data],
  );

  useFillViewport(feedInfiniteQuery);

  // Only the batch loaded on first render of the current scope/filter gets an
  // entrance animation — cards appended afterward via infinite scroll (or the
  // current batch, once already settled) should never animate again.
  const feedGenerationKey = JSON.stringify([contentTypeFilter, scope]);
  const initialBatchSize = useInitialBatchSize(feed, feedGenerationKey);

  return (
    <Container maxWidth="sm" disableGutters sx={{ pb: 2 }}>
      <ExploreSecondaryHeader
        toggleDrawer={toggleDrawer}
        drawerWidth={drawerWidth}
        activeFilter={contentTypeFilter}
        basePath="/explore/activity"
      />

      {/* Section header: title + Everyone/Following toggle, with an expandable blurb */}
      <Box
        sx={{
          // p: 1,
          // bgcolor: "secondary.main",
          mb: 2,
          backgroundImage: "none",
          borderRadius: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Recent Activity
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: -0.5 }}
          >
            {scope === "everyone"
              ? "from everyone"
              : "from accounts you follow"}
          </Typography>
        </Box>
        <HeaderToggleButtonGroup
          value={scope}
          exclusive
          size="small"
          onChange={handleScopeChange}
          aria-label="feed scope"
        >
          {SCOPE_OPTIONS.map(({ value, label, icon }) => (
            <HeaderToggleButton
              key={value}
              value={value}
              aria-label={label}
              sx={{ textTransform: "none" }}
            >
              {React.cloneElement(icon, {
                fontSize: "small",
                sx: { mr: compactToggle ? 0 : 0.5 },
              })}
              {!compactToggle && label}
            </HeaderToggleButton>
          ))}
        </HeaderToggleButtonGroup>
      </Box>

      {feedInfiniteQuery.isError && <Offline />}

      {feedInfiniteQuery.isPending &&
        [1, 2, 3, 4].map((item) => <ActivitySkeleton key={item} />)}

      {feed.length === 0 && feedInfiniteQuery.isSuccess && (
        <EmptyActivity
          variant={scope === "following" ? "noFollowers" : "noActivities"}
          onOpenUserSearch={() => setUserSearchOpen(true)}
        />
      )}

      {feedInfiniteQuery.isSuccess && feed.length > 0 && (
        <InfiniteScroll
          dataLength={feed.length}
          next={feedInfiniteQuery.fetchNextPage}
          hasMore={!!feedInfiniteQuery.hasNextPage}
          loader={
            <Box
              sx={{
                pt: 2,
                pb: 2,
                display: "flex",
                width: "100%",
                justifyContent: "center",
              }}
            >
              <Loading inline />
            </Box>
          }
          style={{ overflow: "visible" }}
        >
          <Grid container spacing={2} id="infinite-scroll-grid">
            {feed.map((activity, index) => {
              if (activity.activity_type === "journal") {
                const card = (
                  <ActivityJournal
                    item={activity}
                    onPosterClick={() =>
                      openContent({
                        contentId: activity.content_id,
                        contentType: activity.content_type,
                        title: activity.content_title,
                      })
                    }
                  />
                );
                return (
                  <Grid item xs={12} key={activity.id}>
                    {index < initialBatchSize ? (
                      <MotionInView inView delay={Math.min(index, 6) * 0.04}>
                        {card}
                      </MotionInView>
                    ) : (
                      card
                    )}
                  </Grid>
                );
              }

              if (
                showTutorial &&
                activity.activity_type === "system" &&
                activity.system_type === "activity_tutorial" &&
                ((isTouchDevice && activity.show_on_mobile) ||
                  (!isTouchDevice && activity.show_on_desktop))
              ) {
                return (
                  <Grid item xs={12} key="tutorial">
                    <ActivityTutorial
                      onDismiss={() => setShowTutorial(false)}
                    />
                  </Grid>
                );
              }

              return null;
            })}
          </Grid>
        </InfiniteScroll>
      )}

      {/* Profile search, opened from the "Following" empty state's CTA. */}
      <StandardDialog
        open={userSearchOpen}
        onClose={() => setUserSearchOpen(false)}
        maxWidth="sm"
        topOffset="10vh"
        disableRestoreFocus
      >
        <DialogContent
          sx={{ border: "none", display: "flex", flexDirection: "column" }}
        >
          <UserSearch
            fullscreen={isTouchDevice}
            onClose={() => setUserSearchOpen(false)}
          />
        </DialogContent>
      </StandardDialog>
    </Container>
  );
}
